import React from 'react';
import { FormGroup, Label, Input, Row, Col } from 'reactstrap';
import TextStyleInputs from './TextStyleInputs';
import ColorPickerInput from './ColorPickerInput';
import { IconX } from '@tabler/icons-react';
import EditableValue from './EditableValue';

const parsePadding = (val) => {
    if (!val) return { value: undefined, unit: 'rem' };
    const num = parseFloat(val);
    const unit = val.replace(String(num), '').trim() || 'rem';
    return { value: num, unit };
};

const getUnitParams = (unit, isHorizontal = false) => {
    switch (unit) {
        case 'px':
            return {
                defaultValue: isHorizontal ? 24 : 48,
                min: 0,
                max: 200,
                step: 1,
            };
        case 'em':
        case 'rem':
        case 'cqh':
        case 'cqw':
            return {
                defaultValue: isHorizontal ? 1.5 : 3,
                min: 0,
                max: 10,
                step: 0.1,
            };
        case '%':
            return {
                defaultValue: isHorizontal ? 2 : 5,
                min: 0,
                max: 20,
                step: 0.1,
            };
        default:
            return {
                defaultValue: isHorizontal ? 1.5 : 3,
                min: 0,
                max: 10,
                step: 0.1,
            };
    }
};

const FooterTab = ({ footer = {}, updateLocalFooterConfig }) => {
    const {
        isFullWidth = false,
        logoUrl = '',
        footerElementsStyle = {},
        backgroundColor = '',
        padding = {},
        gap = {},
    } = footer;

    const paddingVertical = padding?.vertical;
    const paddingHorizontal = padding?.horizontal;
    const gapValueDesktop = gap?.desktop;
    const gapValueMobile = gap?.mobile;

    const verticalPaddingParsed = parsePadding(paddingVertical);
    const horizontalPaddingParsed = parsePadding(paddingHorizontal);
    const gapDesktopParsed = parsePadding(gapValueDesktop);
    const gapMobileParsed = parsePadding(gapValueMobile);

    const vParams = getUnitParams(verticalPaddingParsed.unit, false);
    const hParams = getUnitParams(horizontalPaddingParsed.unit, true);
    const gdParams = getUnitParams(gapDesktopParsed.unit, false);
    const gmParams = getUnitParams(gapMobileParsed.unit, false);

    return (
        <div>
            {/* Section: Layout */}
            <div className="mb-4">
                <h2 className="h6 text-uppercase text-muted fw-bold border-bottom pb-2 mb-3">Layout & Logo</h2>
                <FormGroup switch className="mb-3">
                    <Input
                        type="switch"
                        id="isFooterMenuFullWidth"
                        checked={isFullWidth}
                        onChange={(e) => updateLocalFooterConfig({ isFullWidth: e.target.checked })}
                    />
                    <Label check for="isFooterMenuFullWidth">
                        Volle Breite für Footer
                    </Label>
                </FormGroup>

                <FormGroup className="mb-3">
                    <Label for="customerLogoUrl">Kunden-Logo-URL</Label>
                    <Input
                        type="text"
                        id="customerLogoUrl"
                        value={logoUrl || ''}
                        onChange={(e) => updateLocalFooterConfig({ logoUrl: e.target.value })}
                        placeholder="z.B. https://example.com/customer.svg"
                    />
                </FormGroup>

                <ColorPickerInput
                    id="footerBackgroundColor"
                    label="Hintergrundfarbe"
                    value={backgroundColor}
                    onChange={(color) => updateLocalFooterConfig({ backgroundColor: color })}
                    placeholder="z.B. #ffffff oder transparent"
                />

                <Row className="g-3">
                    <Col xs="12" md="6">
                        <FormGroup className="mb-3">
                            <div className="d-flex justify-content-between mb-2">
                                <Label className="fw-semibold" for="footerPaddingVertical">
                                    Vertikales Padding
                                </Label>
                                <EditableValue
                                    value={verticalPaddingParsed.value}
                                    defaultValue={vParams.defaultValue}
                                    onChange={(val) => {
                                        if (val === undefined) {
                                            updateLocalFooterConfig({
                                                padding: {
                                                    ...(padding || {}),
                                                    vertical: undefined,
                                                },
                                            });
                                        } else {
                                            updateLocalFooterConfig({
                                                padding: {
                                                    ...(padding || {}),
                                                    vertical: `${val}${verticalPaddingParsed.unit}`,
                                                },
                                            });
                                        }
                                    }}
                                    min={vParams.min}
                                    max={vParams.max}
                                    unit={verticalPaddingParsed.unit}
                                    units={['px', 'em', 'rem', 'cqh', 'cqw', '%']}
                                    onUnitChange={(newUnit) => {
                                        const currentVal =
                                            verticalPaddingParsed.value != null
                                                ? verticalPaddingParsed.value
                                                : getUnitParams(newUnit, false).defaultValue;
                                        updateLocalFooterConfig({
                                            padding: {
                                                ...(padding || {}),
                                                vertical: `${currentVal}${newUnit}`,
                                            },
                                        });
                                    }}
                                />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Input
                                    type="range"
                                    id="footerPaddingVertical"
                                    className="flex-grow-1"
                                    min={vParams.min}
                                    max={vParams.max}
                                    step={vParams.step}
                                    value={
                                        verticalPaddingParsed.value != null
                                            ? verticalPaddingParsed.value
                                            : vParams.defaultValue
                                    }
                                    onChange={(e) => {
                                        updateLocalFooterConfig({
                                            padding: {
                                                ...(padding || {}),
                                                vertical: `${parseFloat(e.target.value)}${verticalPaddingParsed.unit}`,
                                            },
                                        });
                                    }}
                                    onPointerDown={() => {
                                        if (verticalPaddingParsed.value == null) {
                                            updateLocalFooterConfig({
                                                padding: {
                                                    ...(padding || {}),
                                                    vertical: `${vParams.defaultValue}${verticalPaddingParsed.unit}`,
                                                },
                                            });
                                        }
                                    }}
                                    style={{
                                        opacity: verticalPaddingParsed.value != null ? 1 : 0.5,
                                        transition: 'opacity 0.15s ease',
                                    }}
                                />
                                <button
                                    type="button"
                                    className={`btn btn-sm p-1 d-flex align-items-center justify-content-center ${
                                        paddingVertical != null
                                            ? 'btn-outline-secondary'
                                            : 'btn-outline-secondary disabled'
                                    }`}
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '4px',
                                        padding: 0,
                                        opacity: paddingVertical != null ? 1 : 0.4,
                                        pointerEvents: paddingVertical != null ? 'auto' : 'none',
                                    }}
                                    onClick={() =>
                                        updateLocalFooterConfig({
                                            padding: {
                                                ...(padding || {}),
                                                vertical: undefined,
                                            },
                                        })
                                    }
                                    title={paddingVertical != null ? 'Wert zurücksetzen' : 'Standardwert aktiv'}
                                >
                                    <IconX size={14} />
                                </button>
                            </div>
                        </FormGroup>
                    </Col>

                    <Col xs="12" md="6">
                        <FormGroup className="mb-3">
                            <div className="d-flex justify-content-between mb-2">
                                <Label className="fw-semibold" for="footerPaddingHorizontal">
                                    Horizontales Padding
                                </Label>
                                <EditableValue
                                    value={horizontalPaddingParsed.value}
                                    defaultValue={hParams.defaultValue}
                                    onChange={(val) => {
                                        if (val === undefined) {
                                            updateLocalFooterConfig({
                                                padding: {
                                                    ...(padding || {}),
                                                    horizontal: undefined,
                                                },
                                            });
                                        } else {
                                            updateLocalFooterConfig({
                                                padding: {
                                                    ...(padding || {}),
                                                    horizontal: `${val}${horizontalPaddingParsed.unit}`,
                                                },
                                            });
                                        }
                                    }}
                                    min={hParams.min}
                                    max={hParams.max}
                                    unit={horizontalPaddingParsed.unit}
                                    units={['px', 'em', 'rem', 'cqh', 'cqw', '%']}
                                    onUnitChange={(newUnit) => {
                                        const currentVal =
                                            horizontalPaddingParsed.value != null
                                                ? horizontalPaddingParsed.value
                                                : getUnitParams(newUnit, true).defaultValue;
                                        updateLocalFooterConfig({
                                            padding: {
                                                ...(padding || {}),
                                                horizontal: `${currentVal}${newUnit}`,
                                            },
                                        });
                                    }}
                                />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Input
                                    type="range"
                                    id="footerPaddingHorizontal"
                                    className="flex-grow-1"
                                    min={hParams.min}
                                    max={hParams.max}
                                    step={hParams.step}
                                    value={
                                        horizontalPaddingParsed.value != null
                                            ? horizontalPaddingParsed.value
                                            : hParams.defaultValue
                                    }
                                    onChange={(e) => {
                                        updateLocalFooterConfig({
                                            padding: {
                                                ...(padding || {}),
                                                horizontal: `${parseFloat(e.target.value)}${horizontalPaddingParsed.unit}`,
                                            },
                                        });
                                    }}
                                    onPointerDown={() => {
                                        if (horizontalPaddingParsed.value == null) {
                                            updateLocalFooterConfig({
                                                padding: {
                                                    ...(padding || {}),
                                                    horizontal: `${hParams.defaultValue}${horizontalPaddingParsed.unit}`,
                                                },
                                            });
                                        }
                                    }}
                                    style={{
                                        opacity: horizontalPaddingParsed.value != null ? 1 : 0.5,
                                        transition: 'opacity 0.15s ease',
                                    }}
                                />
                                <button
                                    type="button"
                                    className={`btn btn-sm p-1 d-flex align-items-center justify-content-center ${
                                        paddingHorizontal != null
                                            ? 'btn-outline-secondary'
                                            : 'btn-outline-secondary disabled'
                                    }`}
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '4px',
                                        padding: 0,
                                        opacity: paddingHorizontal != null ? 1 : 0.4,
                                        pointerEvents: paddingHorizontal != null ? 'auto' : 'none',
                                    }}
                                    onClick={() =>
                                        updateLocalFooterConfig({
                                            padding: {
                                                ...(padding || {}),
                                                horizontal: undefined,
                                            },
                                        })
                                    }
                                    title={paddingHorizontal != null ? 'Wert zurücksetzen' : 'Standardwert aktiv'}
                                >
                                    <IconX size={14} />
                                </button>
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </div>

            {/* Section: Gap */}
            <div className="mb-4">
                <h2 className="h6 text-uppercase text-muted fw-bold border-bottom pb-2 mb-3">
                    Abstand zum Inhalt (Gap)
                </h2>
                <Row className="g-3">
                    <Col xs="12" md="6">
                        <FormGroup className="mb-3">
                            <div className="d-flex justify-content-between mb-2">
                                <Label className="fw-semibold" for="footerGapValueDesktop">
                                    Desktop Abstand (Gap)
                                </Label>
                                <EditableValue
                                    value={gapDesktopParsed.value}
                                    defaultValue={gdParams.defaultValue}
                                    onChange={(val) => {
                                        if (val === undefined) {
                                            updateLocalFooterConfig({
                                                gap: {
                                                    ...(gap || {}),
                                                    desktop: undefined,
                                                },
                                            });
                                        } else {
                                            updateLocalFooterConfig({
                                                gap: {
                                                    ...(gap || {}),
                                                    desktop: `${val}${gapDesktopParsed.unit}`,
                                                },
                                            });
                                        }
                                    }}
                                    min={gdParams.min}
                                    max={gdParams.max}
                                    unit={gapDesktopParsed.unit}
                                    units={['px', 'em', 'rem', 'cqh', 'cqw', '%']}
                                    onUnitChange={(newUnit) => {
                                        const currentVal =
                                            gapDesktopParsed.value != null
                                                ? gapDesktopParsed.value
                                                : getUnitParams(newUnit, false).defaultValue;
                                        updateLocalFooterConfig({
                                            gap: {
                                                ...(gap || {}),
                                                desktop: `${currentVal}${newUnit}`,
                                            },
                                        });
                                    }}
                                />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Input
                                    type="range"
                                    id="footerGapValueDesktop"
                                    className="flex-grow-1"
                                    min={gdParams.min}
                                    max={gdParams.max}
                                    step={gdParams.step}
                                    value={
                                        gapDesktopParsed.value != null ? gapDesktopParsed.value : gdParams.defaultValue
                                    }
                                    onChange={(e) => {
                                        updateLocalFooterConfig({
                                            gap: {
                                                ...(gap || {}),
                                                desktop: `${parseFloat(e.target.value)}${gapDesktopParsed.unit}`,
                                            },
                                        });
                                    }}
                                    onPointerDown={() => {
                                        if (gapDesktopParsed.value == null) {
                                            updateLocalFooterConfig({
                                                gap: {
                                                    ...(gap || {}),
                                                    desktop: `${gdParams.defaultValue}${gapDesktopParsed.unit}`,
                                                },
                                            });
                                        }
                                    }}
                                    style={{
                                        opacity: gapDesktopParsed.value != null ? 1 : 0.5,
                                        transition: 'opacity 0.15s ease',
                                    }}
                                />
                                <button
                                    type="button"
                                    className={`btn btn-sm p-1 d-flex align-items-center justify-content-center ${
                                        gapValueDesktop != null
                                            ? 'btn-outline-secondary'
                                            : 'btn-outline-secondary disabled'
                                    }`}
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '4px',
                                        padding: 0,
                                        opacity: gapValueDesktop != null ? 1 : 0.4,
                                        pointerEvents: gapValueDesktop != null ? 'auto' : 'none',
                                    }}
                                    onClick={() =>
                                        updateLocalFooterConfig({
                                            gap: {
                                                ...(gap || {}),
                                                desktop: undefined,
                                            },
                                        })
                                    }
                                    title={gapValueDesktop != null ? 'Wert zurücksetzen' : 'Standardwert aktiv'}
                                >
                                    <IconX size={14} />
                                </button>
                            </div>
                        </FormGroup>
                    </Col>

                    <Col xs="12" md="6">
                        <FormGroup className="mb-3">
                            <div className="d-flex justify-content-between mb-2">
                                <Label className="fw-semibold" for="footerGapValueMobile">
                                    Mobiler Abstand (Gap)
                                </Label>
                                <EditableValue
                                    value={gapMobileParsed.value}
                                    defaultValue={gmParams.defaultValue}
                                    onChange={(val) => {
                                        if (val === undefined) {
                                            updateLocalFooterConfig({
                                                gap: {
                                                    ...(gap || {}),
                                                    mobile: undefined,
                                                },
                                            });
                                        } else {
                                            updateLocalFooterConfig({
                                                gap: {
                                                    ...(gap || {}),
                                                    mobile: `${val}${gapMobileParsed.unit}`,
                                                },
                                            });
                                        }
                                    }}
                                    min={gmParams.min}
                                    max={gmParams.max}
                                    unit={gapMobileParsed.unit}
                                    units={['px', 'em', 'rem', 'cqh', 'cqw', '%']}
                                    onUnitChange={(newUnit) => {
                                        const currentVal =
                                            gapMobileParsed.value != null
                                                ? gapMobileParsed.value
                                                : getUnitParams(newUnit, false).defaultValue;
                                        updateLocalFooterConfig({
                                            gap: {
                                                ...(gap || {}),
                                                mobile: `${currentVal}${newUnit}`,
                                            },
                                        });
                                    }}
                                />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Input
                                    type="range"
                                    id="footerGapValueMobile"
                                    className="flex-grow-1"
                                    min={gmParams.min}
                                    max={gmParams.max}
                                    step={gmParams.step}
                                    value={
                                        gapMobileParsed.value != null ? gapMobileParsed.value : gmParams.defaultValue
                                    }
                                    onChange={(e) => {
                                        updateLocalFooterConfig({
                                            gap: {
                                                ...(gap || {}),
                                                mobile: `${parseFloat(e.target.value)}${gapMobileParsed.unit}`,
                                            },
                                        });
                                    }}
                                    onPointerDown={() => {
                                        if (gapMobileParsed.value == null) {
                                            updateLocalFooterConfig({
                                                gap: {
                                                    ...(gap || {}),
                                                    mobile: `${gmParams.defaultValue}${gapMobileParsed.unit}`,
                                                },
                                            });
                                        }
                                    }}
                                    style={{
                                        opacity: gapMobileParsed.value != null ? 1 : 0.5,
                                        transition: 'opacity 0.15s ease',
                                    }}
                                />
                                <button
                                    type="button"
                                    className={`btn btn-sm p-1 d-flex align-items-center justify-content-center ${
                                        gapValueMobile != null
                                            ? 'btn-outline-secondary'
                                            : 'btn-outline-secondary disabled'
                                    }`}
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '4px',
                                        padding: 0,
                                        opacity: gapValueMobile != null ? 1 : 0.4,
                                        pointerEvents: gapValueMobile != null ? 'auto' : 'none',
                                    }}
                                    onClick={() =>
                                        updateLocalFooterConfig({
                                            gap: {
                                                ...(gap || {}),
                                                mobile: undefined,
                                            },
                                        })
                                    }
                                    title={gapValueMobile != null ? 'Wert zurücksetzen' : 'Standardwert aktiv'}
                                >
                                    <IconX size={14} />
                                </button>
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </div>

            {/* Section: Footer Style */}
            <div className="mb-4">
                <h2 className="h6 text-uppercase text-muted fw-bold border-bottom pb-2 mb-3">
                    Footer-Schriftgestaltung
                </h2>
                <TextStyleInputs
                    label="Footer-Elemente Textstil"
                    styleConfig={footerElementsStyle}
                    onChange={(style) => updateLocalFooterConfig({ footerElementsStyle: style })}
                />
            </div>
        </div>
    );
};

export default FooterTab;
